import {
  Badge,
  Body,
  Heading,
  Icon,
  TableData,
} from "@elaniintc/goes-design-system";
import { tableColumns } from "./form-detail-table";
import { useLocation, useNavigate, useParams } from "react-router";
import { useGetFormDetail2 } from "@/queries/formaly-request";
import {
  FORM_ASSIGNMENTS_STATUS,
  type FormDetailResponse,
  type FormType,
} from "@/types/entities/formality-request";
import { useEffect, useMemo, useState } from "react";
import DenyReasonShowModal from "./deny-reason-show-modal";
import { cn } from "@/utils/theme";
import { getLastRevisionComments } from "@/utils/procedures-ui";

type AssignmentType =
  | FormDetailResponse["form_assignments"][number]
  | FormDetailResponse["user_form_assignments"][number];

const SectionList = ({
  stepTitle,
  data,
  assigment,
}: {
  stepTitle: string;
  data: FormDetailResponse["steps"][number]["sections"];
  assigment?: AssignmentType;
}) => {
  const [denyReason, setDenyReason] = useState<string>("");

  const columns = useMemo(() => {
    return tableColumns(
      assigment as FormDetailResponse["form_assignments"][number],
      (reason: string) => setDenyReason(reason),
    );
  }, [assigment]);

  return (
    <div className="border-[0.5px] border-neutral-quiet rounded-2xl overflow-hidden">
      <div className="p-3 bg-neutral-object border-b border-neutral-quiet text-body-loud">
        <Heading variant="h6">{stepTitle}</Heading>
      </div>
      {data.map((section, index) => {
        // Filtrar campos condicionados que están vacíos
        const visibleFields = section.fields.filter((field) => {
          const isConditionalField = (field as { is_conditional?: boolean })
            .is_conditional;
          // Si es condicional y no tiene valores, no mostrarlo
          if (
            isConditionalField &&
            (!field.filled_fields || field.filled_fields.length === 0)
          ) {
            return false;
          }
          return true;
        });

        return (
          <div key={section.id}>
            {denyReason && (
              <DenyReasonShowModal
                isOpen={!!denyReason}
                onAccept={() => {
                  setDenyReason("");
                }}
                onClose={() => setDenyReason("")}
                defaultReason={denyReason}
              />
            )}
            <div className="py-1.5 px-3 text-body-loud font-semibold text-xs">
              <h6 className="uppercase" onClick={() => console.log(section)}>
                {section.title}
              </h6>
            </div>

            <TableData
              columns={columns}
              data={visibleFields}
              stripped
              emptyState={<span></span>}
              wrapperClassName={cn(
                "[&_thead]:hidden overflow-x-hidden",
                index === data.length - 1 && "rounded-b-2xl shadow-custom",
              )}
            />
          </div>
        );
      })}
    </div>
  );
};

export const FormDetail = () => {
  const params = useParams<{ formId: string; procedureId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { data, isPending, isLoading, isFetching } = useGetFormDetail2(
    params.formId!,
    params.procedureId!,
  );

  // Guard: si la data cargó pero no hay assignments, la card estaba bloqueada
  useEffect(() => {
    // Esperar a que finalice cualquier carga/refetch antes de evaluar
    if (isPending || isLoading || isFetching || !data) return;
    const hasFormAssignments =
      data.form_assignments && data.form_assignments.length > 0;
    const hasUserFormAssignments =
      data.user_form_assignments && data.user_form_assignments.length > 0;
    if (!hasFormAssignments && !hasUserFormAssignments) {
      const isExpedientes = location.pathname.includes("/expedientes/");
      const backUrl = isExpedientes
        ? location.pathname.replace(/\/[^/]+$/, "") // quita el /:formId final
        : `/tramite/${params.procedureId}`;
      navigate(backUrl, { replace: true });
    }
  }, [
    data,
    isPending,
    isLoading,
    isFetching,
    navigate,
    params.procedureId,
    location.pathname,
  ]);

  const activeAssignments = useMemo(() => {
    if (!data) return [];

    if (data.form_assignments && data.form_assignments.length > 0) {
      return data.form_assignments;
    }
    return data.user_form_assignments || [];
  }, [data]);

  const currentStatusAssignment = activeAssignments[0];
  const latestAssignment = activeAssignments[activeAssignments.length - 1];

  const revisionComment = useMemo(() => {
    return getLastRevisionComments(data as FormType);
  }, [data]);

  if (isPending || isLoading || isFetching || !data) {
    return (
      <div>
        <Icon
          name="spinner-third"
          type="fas"
          className="text-on-disabled animate-spin"
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <div className="flex justify-between items-center">
        <div>
          <Heading variant="h6">{data.name}</Heading>
          <Body>Revisa las respuestas del formulario.</Body>
        </div>

        {currentStatusAssignment?.status ===
          FORM_ASSIGNMENTS_STATUS.FILL_CORRECTION && (
          <Badge size="xs" variant="warning">
            Observado
            <Icon type="fas" name="file-edit" className="text-warning-quiet" />
          </Badge>
        )}

        {currentStatusAssignment?.status ===
          FORM_ASSIGNMENTS_STATUS.FAILED_CORRECTION && (
          <Badge size="xs" variant="danger">
            Rechazado
            <Icon type="fas" name="times" className="text-danger-quiet" />
          </Badge>
        )}
      </div>

      {revisionComment &&
        currentStatusAssignment?.status !== FORM_ASSIGNMENTS_STATUS.COMPLETED &&
        currentStatusAssignment?.status !==
          FORM_ASSIGNMENTS_STATUS.SUBMITTED && (
          <div className="bg-surface-warning-accent-quiet border rounded-lg border-contextual-warning-quiet p-2 flex flex-col gap-1">
            <Heading className="text-size-body-sm leading-2xl">
              Comentario general
            </Heading>
            <Body className="text-size-body-xs">{revisionComment}</Body>
          </div>
        )}

      {/* steps */}
      <div className="flex flex-col gap-6">
        {data.steps
          .filter((step) => {
            return step.sections.some((section) => {
              return section.fields.some((field) => {
                // Verificar si el campo tiene filled_fields y si no está vacío
                if (field.filled_fields && field.filled_fields.length > 0) {
                  return true;
                }
                // Si no tiene filled_fields, mostrarlo por defecto
                return false;
              });
            });
          })
          .map((step) => (
            <SectionList
              key={step.id}
              stepTitle={step.name}
              data={step.sections.filter((section) => {
                return section.fields.some((field) => {
                  if (field.filled_fields && field.filled_fields.length > 0) {
                    return true;
                  }
                  return false;
                });
              })}
              assigment={latestAssignment}
            />
          ))}
      </div>
    </div>
  );
};
